/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps, ICommandBarProps, ICommandBarStyles } from '@fluentui/react/lib/CommandBar';
import { ContextualMenuItemType, IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { qnaMakerTeachingBubbleMessages } from 'src/qnaMaker/core/utils/qnaMakerTeachingBubbleMessages';

export type QnaMakerAzureSearchCommandBarProps = TableCommandBarProps<object> & {
    isLoading: boolean;
    onChangeAzureSearchService: (id: string) => void;
    onCreateAzureSearchService: () => void;
};

const messages = defineMessages({
    selectAzureSearchResource: {
        id: 'languageStudio.qnaMakerAzureSearchCommandBar.selectAzureSearchResource',
        defaultMessage: 'Select Azure Search resource'
    },
    addAzureSearchResource: {
        id: 'languageStudio.qnaMakerAzureSearchCommandBar.addAzureSearchResource',
        defaultMessage: 'Add Azure Search resource'
    }
});

const commandBarTheme = getScopedTheme('QnaMakerAzureSearchCommandBar');

const commandBarStyles = classNamesFunction<ICommandBarProps, ICommandBarStyles>()({
    root: { borderBottom: `1px solid ${commandBarTheme.topBorderColor}` }
});

export const QnaMakerAzureSearchCommandBar = observer((props: QnaMakerAzureSearchCommandBarProps) => {
    const { onChangeAzureSearchService, onCreateAzureSearchService, isLoading } = props;
    const { localize } = useLocalization();

    const { uiStore, rootStore } = useGlobalState();
    const azureSession = rootStore.azureStore.session;
    const azureSearchServices = rootStore.azureStore.searchServicesStoreHouse.items
        .filter(service => service.path !== azureSession.csAccountStore?.ownedSearchResourceId)
        .sort((a, b) => (a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ? 1 : -1));

    const isResourceConnectedToAzureSearch =
        Boolean(azureSession.subscriptionId) &&
        Boolean(azureSession.csAccountStore?.path) &&
        azureSession.csAccountStore?.kind === 'TextAnalytics' &&
        Boolean(azureSession.csAccountStore?.ownedSearchResourceId);

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const azureDropdownIdRef = React.useRef<string>(getId());

    React.useEffect(
        () => () => {
            dequeueTeachingBubble('QnaSelectAzureSearch');
        },
        []
    );

    React.useEffect(() => {
        if (!isResourceConnectedToAzureSearch && canWrite && !isLoading && !uiStore.teachingBubbles.QnaSelectAzureSearch) {
            createTeachingBubble(
                'QnaSelectAzureSearch',
                localize(qnaMakerTeachingBubbleMessages.selectAzureSearchBody),
                localize(qnaMakerTeachingBubbleMessages.selectAzureSearchTitle),
                `#${azureDropdownIdRef.current}`,
                true
            );
        }
    }, [isResourceConnectedToAzureSearch, canWrite, isLoading]);

    const azureSearchServicesOptions: IContextualMenuProps = {
        items: [
            ...azureSearchServices.map(service => ({
                key: service.id,
                name: service.name,
                onClick: () => onChangeAzureSearchService(service.path)
            })),
            { key: 'divider', text: '', itemType: ContextualMenuItemType.Divider },
            {
                key: 'create',
                text: localize(messages.addAzureSearchResource),
                style: { color: commandBarTheme.actionButtonColor },
                onClick: onCreateAzureSearchService
            }
        ]
    };

    const items: ICommandBarItemProps[] = [
        {
            key: 'list',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="azureSearchServicesListToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            aria-haspopup="menu"
                            id={azureDropdownIdRef.current}
                            text={localize(messages.selectAzureSearchResource)}
                            name={localize(messages.selectAzureSearchResource)}
                            disabled={!canWrite || item.disabled || isLoading}
                            ariaLabel={localize(messages.selectAzureSearchResource)}
                            data-automation-id="azureSearchServicesListButton"
                            menuProps={azureSearchServicesOptions}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        ...(isLoading ? [{ key: 'spinner', onRender: () => <Spinner /> }] : [])
    ];

    return <LuisCommandBar items={items} styles={commandBarStyles} showTopBorder={false} />;
});
